// PR-NEOM-2 — full maintenance history for one truck, opened by clicking a
// vehicle plate in the maintenance list.

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, Truck } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/Sheet';
import { apiClient, extractErrorMessage } from '@/lib/api';

export interface VehicleHistoryEvent {
  id: string;
  code: string;
  event_type: string;
  status: string;
  reported_at: string | null;
  total_cost_sar: string | null;
  photo_count?: number;
  driver_name?: string | null;
}

const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function VehicleHistorySheet({
  vehicleId,
  plate,
  typeLabel,
  onClose,
}: {
  vehicleId: string;
  plate: string | null;
  typeLabel: (t: string) => string;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<VehicleHistoryEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    apiClient.get<VehicleHistoryEvent[]>(`/api/v1/maintenance/events?vehicleId=${vehicleId}&limit=500`)
      .then((r) => { if (!cancelled) { setRows(r.data ?? []); setError(null); } })
      .catch((err) => { if (!cancelled) setError(extractErrorMessage(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [vehicleId]);

  const totalSpend = rows
    .filter((r) => r.status !== 'cancelled')
    .reduce((sum, r) => sum + Number(r.total_cost_sar ?? 0), 0);

  return (
    <Sheet open onOpenChange={(o) => { if (!o) onClose(); }}>
      <SheetContent side="right" className="sm:max-w-lg">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5" /> <span className="font-mono" dir="ltr">{plate ?? 'Vehicle'}</span> · maintenance history
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 overflow-y-auto p-6 space-y-3" data-vehicle-history>
          {loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : error ? (
            <div className="rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-700 dark:text-rose-400 p-3 text-sm">{error}</div>
          ) : rows.length === 0 ? (
            <div className="rounded-lg border border-border p-8 text-center text-sm text-muted-foreground">No maintenance recorded for this truck yet.</div>
          ) : (
            <>
              <div className="flex items-center justify-between text-sm">
                <span className="text-muted-foreground">{rows.length} job{rows.length === 1 ? '' : 's'}</span>
                <span className="font-medium">Total {fmtSar(totalSpend)}</span>
              </div>
              <div className="space-y-2">
                {rows.map((e) => (
                  <Link key={e.id} to={`/dashboard/maintenance/${e.id}`}
                    className="block rounded-lg border border-border p-3 hover:bg-muted/40 transition-colors">
                    <div className="flex items-baseline justify-between gap-2">
                      <div className="font-medium text-sm">{typeLabel(e.event_type)}</div>
                      <div className="text-xs text-muted-foreground font-mono">{(e.reported_at ?? '').slice(0, 10) || '—'}</div>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-3">
                      <span>Status: {e.status}</span>
                      {e.driver_name && <span>Driver: {e.driver_name}</span>}
                      {Number(e.total_cost_sar ?? 0) > 0 && <span>{fmtSar(Number(e.total_cost_sar))}</span>}
                      {(e.photo_count ?? 0) > 0 && <span>{e.photo_count} photo{(e.photo_count ?? 0) === 1 ? '' : 's'}</span>}
                    </div>
                  </Link>
                ))}
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
